import {useParams} from "react-router";
import Form from "../../form/Form";
import Modal from "../../form/Modal";
import {DataTableColumn} from "../../datatable/DataTableTypes";
import {DataTable} from "../../datatable/DataTable";
import {
    useGetAllDocumentsQuery,
    useGetTgxFilesByRelationIdQuery,
    useLazyDonwLoadFilesQuery,
    useLoadFilesMutation
} from "../DocumentsApi";
import ButtonTray from "../../form/ButtonTray";
import React, {FormEvent, useEffect, useState} from "react";

interface Document {
    id: string;
    filename: string;
    tgxFileType?: string;
    fileType?: string;
    fileSize: number;
}

interface MultiSelectDocModalProps {
    show: boolean;
    setShow: (value: boolean) => void;
    documentTypeList?: string[];
    headContent?: string;
    path?: string;
}

export default function MultiSelectDocModal({
                                                show,
                                                setShow,
                                                documentTypeList,
                                                headContent,
                                            }: MultiSelectDocModalProps) {


    const [docList, setDocList] = useState<Document[]>([]);
    const [nonViewable, setNonViewable] = useState(false);
    const {collectorId, orderId, associationId, subdivisionId} = useParams();
    const relationId = collectorId || orderId || associationId || subdivisionId;
    const [loadFiles] = useLoadFilesMutation();
    const [downloadFiles, {isFetching}] = useLazyDonwLoadFilesQuery();
    const customFilter = {
        sortOrder: "desc",
        orderId: relationId,
        orderFileType: documentTypeList ? documentTypeList : [],
        relationalId: relationId
    };

    const {data: documentData} = useGetTgxFilesByRelationIdQuery({
        ...customFilter,
        "sortOrder": "desc",
    });

    const viewableFileExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.txt'];

    const isViewableFile = (fileName: any) => {
        const fileExtension = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
        return viewableFileExtensions.includes(fileExtension);
    };

    useEffect(() => {
        if (documentData?.data?.length > 0) {
            const firstFile = documentData?.data[0];
            if (isViewableFile(firstFile.filename)) {

                loadFiles(firstFile.id);
            } else {

                setNonViewable(true); // Indicate the file isn't viewable
            }
        }
    }, [documentData]);


    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            await Promise.all(
                docList.map((el: any) =>
                    downloadFiles({fileId: el?.id, isDownload: true}).unwrap() // Assuming viewFile returns a promise
                )
            );
        } catch (error) {
            console.error("Error downloading files:", error);
        }
        setShow(false);
        setDocList([]);
    }

    const columns: DataTableColumn[] = [
        {
            label: "",
            accessor: "id", canSort: false,
            cell: (props: any) => {
                return <>

                    <input
                        type="checkbox"
                        id={props.row.original.id}
                        checked={docList.some((e: any) => e.id === props.row.original.id)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const isChecked = e.target.checked;
                            const fileId = props.row.original.id;

                            if (isChecked) {
                                // Add if not already in list
                                if (!docList.some((f: any) => f.id === fileId)) {
                                    setDocList([...docList, props.row.original]);
                                }
                            } else {
                                // Remove if in list
                                setDocList(docList.filter((f: any) => f.id !== fileId));
                            }

                        }}
                        onClick={() => {
                            const file = props.row.original;
                            if (isViewableFile(file.filename)) {
                                loadFiles(file.id);
                                setNonViewable(false);
                            } else {
                                setNonViewable(true);
                            }
                        }}
                    />

                </>

            }
        },
        {
            label: "Name",
            accessor: "filename",

        },
        {
            label: "Type",
            accessor: headContent === "Documents" ? "fileType" : "tgxFileType",

        }
    ];


    return (
        <Modal
            title="Download Documents"
            show={show}
            setShow={setShow}
            className="w-5/6"
            fields={
                <Form onSubmit={handleSubmit} fields={
                    <>

                        <div className="modal-body">
                            <p className="ml-1">Select the documents you would like to download</p>
                            <div className="md:grid sm:block grid-cols-12 gap-3">
                                <div className="col-span-5">
                                    <DataTable
                                        columns={columns}
                                        defaultFilter={{
                                            ...customFilter,
                                            "sortOrder": "desc",
                                        }}
                                        actions={{
                                            fetchAction: headContent === "Documents" ? useGetAllDocumentsQuery : useGetTgxFilesByRelationIdQuery
                                        }}
                                        renderOptions={{
                                            skipUserSettingRender: true,
                                        }}
                                        appendFilterToUrl={false}

                                    />

                                </div>
                                <div className="col-span-7">
                                    {nonViewable ? (
                                        <div style={{
                                            width: '100%',
                                            minHeight: '500px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '1px solid #ddd'
                                        }}>
                                            <p>This file type cannot be displayed. Please download to view.</p>
                                        </div>
                                    ) : (
                                        <iframe src="" style={{width: '100%', minHeight: '500px', overflow: 'visible'}}
                                                width="100%" height="100%" scrolling="no" frameBorder="0"/>
                                    )}
                                </div>
                            </div>
                        </div>


                        <div className={"border-t border-surface-5 flex justify-end mt-4"}>
                            <ButtonTray
                                buttons={[
                                    {
                                        buttonProps: {
                                            btnText: "Cancel",
                                            onClick: () => setShow(false),
                                            type: "close",
                                        },
                                        buttonType: "button"
                                    },
                                    {
                                        buttonProps: {
                                            btnText: "Download",
                                            type: "submit",
                                            isLoading: isFetching,
                                            isDisabled: docList.length <= 0
                                        },
                                        buttonType: "button",

                                    }
                                ]}
                                align="end"
                                gap={2}
                            />
                        </div>

                    </>
                }
                />

            }
        />

    );
}